import { createFileRoute, Link } from "@tanstack/react-router";
import {
  Phone,
  ShieldCheck,
  Calendar,
  Handshake,
  Brush,
  Hammer,
  Wrench,
  TreePine,
  ArrowRight,
  CheckCircle2,
  ChevronDown,
  Star,
  Sparkles,
} from "lucide-react";
import { DeferredHeroGlow, DeferredHeroVideo } from "@/components/site/DeferredHeroVideo";
import { Layout } from "@/components/site/Layout";
import { Picture } from "@/components/site/Picture";
import { Reveal } from "@/components/site/Reveal";
import { Timeline } from "@/components/site/Timeline";
import { Gallery } from "@/components/site/Gallery";
import { CoverageMap } from "@/components/site/CoverageMap";
import { FAQ, faqs as homeFaqs } from "@/components/site/HomeFAQ";
import heroVideoWebm from "@/assets/optimized/hero-painter-1280.webm";
import heroVideoMp4 from "@/assets/optimized/hero-painter-1280.mp4";
import {
  heroHomeImage,
  heroHomePreloadHref,
  trustExperienceImage,
  trustHonestImage,
  trustOnsiteImage,
} from "@/lib/images";

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "The Crew Paint Contractors — Painting & Siding in Arlington, TX" },
      {
        name: "description",
        content:
          "Honest residential painting and siding/wood replacement in Arlington, TX. 40+ years of experience. Call Bart at 682-454-5997.",
      },
      { property: "og:title", content: "The Crew Paint Contractors — Arlington, TX" },
      {
        property: "og:description",
        content: "40+ years of honest craftsmanship. Painting, siding, and wood replacement done right.",
      },
    ],
    links: [
      {
        rel: "preload",
        href: heroHomePreloadHref,
        as: "image",
        type: "image/avif",
        imageSrcSet: heroHomeImage.avifSrcSet,
        imageSizes: heroHomeImage.sizes,
        fetchPriority: "high",
      },
      { rel: "canonical", href: "https://thecrewpainting.com/" },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          name: "The Crew Paint Contractors",
          description:
            "Residential painting, siding and wood replacement in Arlington, TX. Owner-operated by Bart Baldwin with 40+ years of experience.",
          telephone: "+1-682-454-5997",
          email: "thecrew1962@gmail.com",
          url: "https://thecrewpainting.com/",
          founder: { "@type": "Person", name: "Bart Baldwin" },
          address: {
            "@type": "PostalAddress",
            addressLocality: "Arlington",
            addressRegion: "TX",
            addressCountry: "US",
          },
          areaServed: [
            "Arlington, TX",
            "Mansfield, TX",
            "Grand Prairie, TX",
            "Kennedale, TX",
            "Fort Worth, TX",
            "Pantego, TX",
          ],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: homeFaqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: Home,
});

// Compressed local loop (1280×720, no audio) — not the 8.8 MB CDN .mov.

function Home() {
  return (
    <Layout>
      <link
        rel="preload"
        as="image"
        type="image/avif"
        href={heroHomePreloadHref}
        imageSrcSet={heroHomeImage.avifSrcSet}
        imageSizes={heroHomeImage.sizes}
        fetchPriority="high"
      />
      {/* ============ HERO ============ */}
      <section className="relative isolate h-[100svh] min-h-[640px] w-full overflow-hidden [contain:layout] bg-primary text-primary-foreground">
        {/* LCP poster stays in static HTML so the request is discoverable
            immediately and is never lazy-loaded. */}
        <Picture
          {...heroHomeImage}
          alt=""
          ariaHidden
          fill
          fetchPriority="high"
          loading="eager"
        />
        <div className="absolute inset-0">
          <DeferredHeroVideo webm={heroVideoWebm} mp4={heroVideoMp4} />
        </div>

        <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-primary/55 via-primary/30 to-primary/80" />
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_30%_40%,_oklch(0.58_0.14_55/0.25),_transparent_60%)]" />

        <DeferredHeroGlow />

        <div className="relative z-10 mx-auto flex h-full max-w-7xl flex-col justify-center px-6">
          <span
            className="hero-fade-up inline-flex w-fit items-center gap-2 rounded-full border border-primary-foreground/25 bg-primary-foreground/10 px-3.5 py-1.5 text-xs font-medium backdrop-blur"
            style={{ animationDelay: "0.1s" }}
          >
            <Sparkles className="h-3.5 w-3.5 text-accent" />
            Serving Arlington, TX since 1984
          </span>

          <h1
            className="hero-fade-up mt-6 max-w-4xl font-display text-5xl font-semibold leading-[1.02] tracking-tight md:text-7xl lg:text-[5.5rem]"
            style={{ animationDelay: "0.2s" }}
          >
            Premium painting,{" "}
            <span className="relative inline-block">
              <span className="relative z-10 text-white">honest craftsmanship.</span>
              <span className="hero-grow-x absolute -bottom-1 left-0 right-0 h-[6px] origin-left rounded-full bg-white/30" />
            </span>
          </h1>

          <p
            className="hero-fade-up mt-7 max-w-xl text-lg text-primary-foreground/85 md:text-xl"
            style={{ animationDelay: "0.45s" }}
          >
            40+ years protecting Arlington homes with residential painting, siding, and wood
            replacement. We show up every day until it's done right.
          </p>

          <div
            className="hero-fade-up mt-10 flex flex-wrap gap-4"
            style={{ animationDelay: "0.65s" }}
          >
            <a
              href="tel:6824545997"
              className="group inline-flex items-center gap-3 rounded-full bg-accent px-7 py-4 text-base font-semibold text-accent-foreground shadow-2xl shadow-accent/30 transition hover:scale-[1.02] hover:shadow-accent/50"
            >
              <Phone className="h-5 w-5" />
              Call 682-454-5997
              <ArrowRight className="h-4 w-4 transition group-hover:translate-x-1" />
            </a>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 rounded-full border border-primary-foreground/30 bg-primary-foreground/5 px-7 py-4 text-base font-semibold backdrop-blur transition hover:bg-primary-foreground/15"
            >
              Get a Free Quote
            </Link>
          </div>

          <div
            className="hero-fade-up mt-14 flex flex-wrap items-center gap-x-8 gap-y-4 text-sm text-primary-foreground/80"
            style={{ animationDelay: "1s" }}
          >
            <div className="flex items-center gap-2">
              <div className="flex">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-4 w-4 fill-accent text-accent" />
                ))}
              </div>
              <span>Trusted by Arlington homeowners</span>
            </div>
            <span className="hidden h-4 w-px bg-primary-foreground/20 md:block" />
            <span>Owner-operated by Bart Baldwin</span>
            <span className="hidden h-4 w-px bg-primary-foreground/20 md:block" />
            <span>Free, no-pressure quotes</span>
          </div>
        </div>

        <div
          className="hero-fade-up absolute bottom-8 left-1/2 z-10 -translate-x-1/2 text-primary-foreground/70"
          style={{ animationDelay: "1.5s" }}
        >
          <div className="hero-bob flex flex-col items-center gap-2 text-xs uppercase tracking-[0.25em]">
            Scroll
            <ChevronDown className="h-4 w-4" />
          </div>
        </div>
      </section>

      {/* ============ VALUE PROPS ============ */}
      <section className="mx-auto max-w-7xl px-6 py-24">
        <Reveal>
          <div className="max-w-2xl">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-accent">
              Why Homeowners Trust The Crew
            </p>
            <h2 className="mt-3 font-display text-4xl text-primary md:text-5xl">
              Built on four decades of doing it right.
            </h2>
          </div>
        </Reveal>

        <div className="mt-14 grid gap-6 md:grid-cols-3">
          {[
            {
              Icon: ShieldCheck,
              title: "40+ Years Experience",
              body: "We stand behind every coat, every board, every detail.",
              image: trustExperienceImage,
              alt: "Experienced painter carefully painting exterior trim at golden hour",
            },
            {
              Icon: Calendar,
              title: "Daily On-Site Presence",
              body: "We show up every day and finish in a timely manner.",
              image: trustOnsiteImage,
              alt: "The Crew arriving on-site with ladders and equipment in the morning",
            },
            {
              Icon: Handshake,
              title: "Honest & Transparent",
              body: "Fair pricing and clear communication from start to finish.",
              image: trustHonestImage,
              alt: "Contractor shaking hands with a homeowner on the front porch",
            },
          ].map(({ Icon, title, body, image, alt }, i) => (
            <Reveal key={title} delay={i * 0.1}>
              <div className="group relative flex h-full flex-col overflow-hidden rounded-2xl border border-border bg-card transition-all hover:-translate-y-1 hover:border-accent/40 hover:shadow-xl hover:shadow-primary/5">
                <div className="relative aspect-[7/5] overflow-hidden">
                  <Picture
                    {...image}
                    alt={alt}
                    loading="lazy"
                    className="transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-card via-card/30 to-transparent" />
                  <div className="absolute bottom-4 left-4 flex h-12 w-12 items-center justify-center rounded-xl bg-primary text-primary-foreground shadow-lg shadow-primary/30">
                    <Icon className="h-6 w-6" />
                  </div>
                </div>
                <div className="flex flex-1 flex-col p-8 pt-6">
                  <h3 className="font-display text-2xl text-primary">{title}</h3>
                  <p className="mt-3 leading-relaxed text-muted-foreground">{body}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ============ TIMELINE ============ */}
      <Timeline />

      {/* ============ SERVICES ============ */}
      <section className="relative overflow-hidden border-y border-border bg-secondary/40">
        <div className="absolute inset-0 -z-10 bg-[radial-gradient(circle_at_80%_20%,_oklch(0.36_0.13_268/0.08),_transparent_50%)]" />
        <div className="mx-auto max-w-7xl px-6 py-24">
          <Reveal>
            <div className="flex flex-wrap items-end justify-between gap-6">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-accent">
                  What We Do
                </p>
                <h2 className="mt-3 font-display text-4xl text-primary md:text-5xl">
                  Core services
                </h2>
              </div>
              <Link
                to="/services"
                className="group inline-flex items-center gap-2 text-sm font-semibold text-primary"
              >
                See all services
                <ArrowRight className="h-4 w-4 transition group-hover:translate-x-1" />
              </Link>
            </div>
          </Reveal>

          <div className="mt-12 grid gap-6 md:grid-cols-3 md:auto-rows-[240px]">
            {/* Featured card spans 2 cols × 2 rows */}
            <Reveal className="md:col-span-2 md:row-span-2">
              <Link
                to="/services"
                className="group relative flex h-full min-h-[480px] flex-col justify-end overflow-hidden rounded-[2.5rem] bg-primary p-10 text-primary-foreground shadow-2xl shadow-primary/10 md:p-12"
              >
                <Picture
                  {...heroHomeImage}
                  alt=""
                  ariaHidden
                  sizes="(min-width: 768px) 66vw, 100vw"
                  loading="lazy"
                  className="absolute inset-0 opacity-40 transition-transform duration-1000 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-primary via-primary/60 to-transparent" />
                <div className="relative z-10 max-w-md">
                  <span className="inline-block rounded-full bg-accent px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-accent-foreground">
                    Featured
                  </span>
                  <h3 className="mt-6 font-display text-4xl leading-tight md:text-5xl">
                    Siding & Wood Replacement
                  </h3>
                  <p className="mt-4 text-sm italic leading-relaxed text-primary-foreground/80">
                    We fix damaged siding and exterior wood to permanently protect your home's
                    value and appearance.
                  </p>
                  <div className="mt-8 inline-flex items-center gap-2 border-b border-primary-foreground/20 pb-1 text-sm font-semibold transition-colors group-hover:border-primary-foreground">
                    View siding &amp; wood replacement services
                    <ArrowRight className="h-4 w-4 transition group-hover:translate-x-1" />
                  </div>
                </div>
              </Link>
            </Reveal>

            {[
              { Icon: Brush, title: "Interior & Exterior Painting" },
              { Icon: Wrench, title: "Drywall Repairs & Texture" },
              { Icon: Hammer, title: "Interior Trim Replacement" },
              { Icon: TreePine, title: "Deck & Fence Staining" },
              { Icon: Sparkles, title: "Pressure Washing" },
            ].map(({ Icon, title }, i) => (
              <Reveal key={title} delay={i * 0.08}>
                <Link
                  to="/services"
                  className="group flex h-full flex-col justify-center rounded-[2rem] border border-border bg-card p-8 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-primary/5"
                >
                  <div className="mb-6 flex h-14 w-14 items-center justify-center rounded-2xl bg-accent/10 text-accent transition-colors duration-300 group-hover:bg-accent group-hover:text-accent-foreground">
                    <Icon className="h-7 w-7" />
                  </div>
                  <h4 className="font-display text-xl leading-snug text-primary">{title}</h4>
                  <p className="mt-2 text-sm text-muted-foreground">Expert craftsmanship.</p>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ============ GALLERY ============ */}
      <Gallery />

      {/* ============ PROCESS ============ */}
      <section className="mx-auto max-w-7xl px-6 py-28">
        <Reveal>
          <div className="mx-auto max-w-2xl text-center">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-accent">
              Our Process
            </p>
            <h2 className="mt-3 font-display text-4xl text-primary md:text-5xl">
              Three simple steps
            </h2>
            <p className="mt-5 text-muted-foreground">
              No runaround. No surprises. Just a clear path from your first call to a finished
              job.
            </p>
          </div>
        </Reveal>

        <div className="relative mt-16 grid gap-10 md:grid-cols-3">
          <div className="absolute left-[16%] right-[16%] top-9 hidden h-px bg-gradient-to-r from-transparent via-border to-transparent md:block" />
          {[
            { n: "01", title: "You Reach Out", body: "Call or send a message. We're easy to get in touch with." },
            { n: "02", title: "We Call You ASAP", body: "Bart follows up immediately to schedule a transparent quote." },
            { n: "03", title: "We Complete the Job", body: "On time, on budget, and with daily progress you can see." },
          ].map((s, i) => (
            <Reveal key={s.n} delay={i * 0.12}>
              <div className="relative text-center">
                <div className="relative z-10 mx-auto flex h-18 w-18 items-center justify-center">
                  <div className="absolute inset-0 rounded-full bg-accent/15 blur-xl" />
                  <div className="relative flex h-18 w-18 items-center justify-center rounded-full border border-border bg-card font-display text-xl text-primary">
                    {s.n}
                  </div>
                </div>
                <h3 className="mt-6 font-display text-2xl text-primary">{s.title}</h3>
                <p className="mx-auto mt-3 max-w-xs text-muted-foreground">{s.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ============ COVERAGE MAP ============ */}
      <CoverageMap />

      {/* ============ FAQ ============ */}
      <FAQ />

      {/* ============ CTA ============ */}
      <section className="mx-auto max-w-7xl px-6 pb-24">
        <Reveal>
          <div className="relative overflow-hidden rounded-3xl bg-primary p-10 text-primary-foreground md:p-16">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_oklch(0.58_0.14_55/0.4),_transparent_55%)]" />
            <div
              aria-hidden
              className="hero-glow absolute -bottom-20 -left-20 h-[320px] w-[320px] rounded-full bg-accent/25 blur-3xl"
            />
            <div className="relative flex flex-col items-start gap-8 md:flex-row md:items-center md:justify-between">
              <div className="max-w-xl">
                <h2 className="font-display text-3xl md:text-5xl">
                  Ready to get your home looking its best?
                </h2>
                <p className="mt-4 text-primary-foreground/80">
                  Talk directly with Bart. No call centers, no pressure — just honest answers.
                </p>
                <ul className="mt-6 grid grid-cols-2 gap-3 text-sm">
                  {["Fair, honest pricing", "Daily on-site presence", "Owner-operated", "Clear communication"].map(
                    (t) => (
                      <li key={t} className="flex items-center gap-2 text-primary-foreground/85">
                        <CheckCircle2 className="h-4 w-4 text-accent" /> {t}
                      </li>
                    )
                  )}
                </ul>
              </div>
              <a
                href="tel:6824545997"
                className="group inline-flex shrink-0 items-center gap-3 rounded-full bg-accent px-8 py-5 text-lg font-semibold text-accent-foreground shadow-2xl shadow-accent/30 transition hover:scale-[1.02]"
              >
                <Phone className="h-5 w-5" />
                682-454-5997
                <ArrowRight className="h-5 w-5 transition group-hover:translate-x-1" />
              </a>
            </div>
          </div>
        </Reveal>
      </section>
    </Layout>
  );
}
